import { FocusArea } from 'libs/end-to-end/src/focus-area';
import { MyObservation } from 'libs/end-to-end/src/my-observation';
import { isHiringCopilotEnabledForRevision } from 'libs/end-to-end/src/common-revision-flags';
import { isPayBenchmarkingEnabledForRevision } from 'libs/end-to-end/src/common-revision-flags';
import { isPayCorrectionEnabledForRevision } from 'libs/end-to-end/src/common-revision-flags';
import { isPromotionCopilotEnabledForRevision } from 'libs/end-to-end/src/common-revision-flags';

export type CopilotCardStackType =
  | 'hiring'
  | 'promotion'
  | 'pay-benchmarking'
  | 'pay-correction';

export interface CopilotCardTypeChecks {
  isEnabled: (myObservation?: MyObservation) => boolean;
  isUpsell: (myObservation?: MyObservation) => boolean;
}

export const CopilotCardStackTypeChecks: Record<
  CopilotCardStackType,
  CopilotCardTypeChecks
> = {
  hiring: {
    isEnabled: (myObservation?: MyObservation) => {
      return isHiringCopilotEnabledForRevision({
        revisionFlags: myObservation ?? null,
      });
    },
    isUpsell: (myObservation?: MyObservation) => {
      return (
        myObservation?.upsells?.some((u) => u.type === 'hiring-copilot') ??
        false
      );
    },
  },
  promotion: {
    isEnabled: (myObservation?: MyObservation) => {
      return isPromotionCopilotEnabledForRevision({
        revisionFlags: myObservation ?? null,
      });
    },
    isUpsell: (myObservation?: MyObservation) => {
      return (
        myObservation?.upsells?.some((u) => u.type === 'promotion-copilot') ??
        false
      );
    },
  },
  'pay-benchmarking': {
    isEnabled: (myObservation?: MyObservation) => {
      return isPayBenchmarkingEnabledForRevision({
        revisionFlags: myObservation ?? null,
      });
    },
    isUpsell: (myObservation?: MyObservation) => {
      return (
        myObservation?.upsells?.some(
          (u) => u.type === 'pay-benchmarking-copilot',
        ) ?? false
      );
    },
  },
  'pay-correction': {
    isEnabled: (myObservation?: MyObservation) => {
      return isPayCorrectionEnabledForRevision({
        revisionFlags: myObservation ?? null,
      });
    },
    isUpsell: (myObservation?: MyObservation) => {
      return (
        myObservation?.upsells?.some(
          (u) => u.type === 'pay-correction-copilot',
        ) ?? false
      );
    },
  },
};

export const FocusAreaToCopilotCardStackTypes: Record<
  FocusArea,
  CopilotCardStackType[]
> = {
  Hiring: ['hiring'],
  Compensation: ['pay-benchmarking'],
  Performance: [],
  Promotion: ['promotion'],
  Attrition: [],
  Engagement: [],
  Leadership: [],
};

const VIOLET_GRADIENT_COLOR =
  'bg-gradient-to-br from-[#930084] from-[-50%] via-black via-50% to-black to-50%';
const AQUA_GRADIENT_COLOR =
  'bg-gradient-to-br from-[#06769d] from-[-50%] via-black via-50% to-black to-50%';
const RASPBERRY_GRADIENT_COLOR =
  'bg-gradient-to-br from-[#d50032] from-[-50%] via-black via-50% to-black to-50%';
const GREEN_GRADIENT_COLOR =
  'bg-gradient-to-br from-[#0b7f0b] from-[-50%] via-black via-50% to-black to-50%';

export interface CopilotCardStackStyle {
  baseBackgroundColor: 'bg-white' | 'bg-black';
  baseGradientColor?:
    | typeof VIOLET_GRADIENT_COLOR
    | typeof AQUA_GRADIENT_COLOR
    | typeof RASPBERRY_GRADIENT_COLOR
    | typeof GREEN_GRADIENT_COLOR;
  textColor: 'text-black' | 'text-white';
  hintColor:
    | 'text-violet-300'
    | 'text-violet-800'
    | 'text-aqua-300'
    | 'text-aqua-600'
    | 'text-raspberry-400'
    | 'text-raspberry-600'
    | 'text-green-300'
    | 'text-green-600';
  title:
    | 'hiringCopilotTitle'
    | 'promotionCopilotTitle'
    | 'payBenchmarkingTitle'
    | 'payCorrectionTitle';
  subtitle:
    | 'copilotTopCandidatesIdentified'
    | 'copilotTopEmployeesIdentified'
    | 'unlockCompetitiveAdvantage';
  description:
    | 'hiringCopilotDescription'
    | 'promotionCopilotDescription'
    | 'payBenchmarkingDescription'
    | 'payCorrectionDescription';
  isometricImagePath: string;
  actionToTakeTitle: 'copilotOpen' | 'copilotEnableIt';
  actionToTake:
    | {
        type: 'navigate';
        link: string;
      }
    | {
        type: 'openEmailModal';
      };
}

export const CopilotCardStackStyles: Record<
  CopilotCardStackType,
  {
    upsell: CopilotCardStackStyle;
    enabled: CopilotCardStackStyle;
  }
> = {
  hiring: {
    enabled: {
      baseBackgroundColor: 'bg-black',
      baseGradientColor: VIOLET_GRADIENT_COLOR,
      textColor: 'text-white',
      hintColor: 'text-violet-300',
      title: 'hiringCopilotTitle',
      subtitle: 'copilotTopCandidatesIdentified',
      description: 'hiringCopilotDescription',
      isometricImagePath: 'images/enabled_hiring_copilot_isometric.svg',
      actionToTakeTitle: 'copilotOpen',
      actionToTake: {
        type: 'navigate',
        link: '/copilot/hiring?action=card',
      },
    },
    upsell: {
      baseBackgroundColor: 'bg-white',
      textColor: 'text-black',
      hintColor: 'text-violet-800',
      title: 'hiringCopilotTitle',
      subtitle: 'copilotTopCandidatesIdentified',
      description: 'hiringCopilotDescription',
      isometricImagePath: 'images/disabled_hiring_copilot_isometric.svg',
      actionToTakeTitle: 'copilotEnableIt',
      actionToTake: {
        type: 'openEmailModal',
      },
    },
  },
  promotion: {
    enabled: {
      baseBackgroundColor: 'bg-black',
      baseGradientColor: RASPBERRY_GRADIENT_COLOR,
      textColor: 'text-white',
      hintColor: 'text-raspberry-400',
      title: 'promotionCopilotTitle',
      subtitle: 'copilotTopEmployeesIdentified',
      description: 'promotionCopilotDescription',
      isometricImagePath: 'images/enabled_promotion_copilot_isometric.svg',
      actionToTakeTitle: 'copilotOpen',
      actionToTake: {
        type: 'navigate',
        link: '/copilot/promotion?action=card',
      },
    },
    upsell: {
      baseBackgroundColor: 'bg-white',
      textColor: 'text-black',
      hintColor: 'text-raspberry-600',
      title: 'promotionCopilotTitle',
      subtitle: 'copilotTopEmployeesIdentified',
      description: 'promotionCopilotDescription',
      isometricImagePath: 'images/disabled_promotion_copilot_isometric.svg',
      actionToTakeTitle: 'copilotEnableIt',
      actionToTake: {
        type: 'openEmailModal',
      },
    },
  },
  'pay-benchmarking': {
    enabled: {
      baseBackgroundColor: 'bg-black',
      baseGradientColor: AQUA_GRADIENT_COLOR,
      textColor: 'text-white',
      hintColor: 'text-aqua-300',
      title: 'payBenchmarkingTitle',
      subtitle: 'unlockCompetitiveAdvantage',
      description: 'payBenchmarkingDescription',
      isometricImagePath: 'images/enabled_pay_benchmarking_isometric.svg',
      actionToTakeTitle: 'copilotOpen',
      actionToTake: {
        type: 'navigate',
        link: '/copilot/pay-benchmarking?action=card',
      },
    },
    upsell: {
      baseBackgroundColor: 'bg-white',
      textColor: 'text-black',
      hintColor: 'text-aqua-600',
      title: 'payBenchmarkingTitle',
      subtitle: 'unlockCompetitiveAdvantage',
      description: 'payBenchmarkingDescription',
      isometricImagePath: 'images/disabled_pay_benchmarking_isometric.svg',
      actionToTakeTitle: 'copilotEnableIt',
      actionToTake: {
        type: 'openEmailModal',
      },
    },
  },
  'pay-correction': {
    enabled: {
      baseBackgroundColor: 'bg-black',
      baseGradientColor: AQUA_GRADIENT_COLOR,
      textColor: 'text-white',
      hintColor: 'text-green-300',
      title: 'payCorrectionTitle',
      subtitle: 'unlockCompetitiveAdvantage',
      description: 'payCorrectionDescription',
      isometricImagePath: 'images/enabled_pay_benchmarking_isometric.svg',
      actionToTakeTitle: 'copilotOpen',
      actionToTake: {
        type: 'navigate',
        link: '/copilot/pay-correction?action=card',
      },
    },
    upsell: {
      baseBackgroundColor: 'bg-white',
      textColor: 'text-black',
      hintColor: 'text-green-600',
      title: 'payCorrectionTitle',
      subtitle: 'unlockCompetitiveAdvantage',
      description: 'payCorrectionDescription',
      isometricImagePath: 'images/disabled_pay_benchmarking_isometric.svg',
      actionToTakeTitle: 'copilotEnableIt',
      actionToTake: {
        type: 'openEmailModal',
      },
    },
  },
};

export const getCopilotStylesToDisplay = (
  focusArea: FocusArea,
  myObservation: MyObservation | null,
): CopilotCardStackStyle[] => {
  const types = FocusAreaToCopilotCardStackTypes[focusArea];
  const styles: CopilotCardStackStyle[] = [];
  for (const type of types) {
    const checks = CopilotCardStackTypeChecks[type];
    if (checks.isEnabled(myObservation ?? undefined)) {
      styles.push(CopilotCardStackStyles[type].enabled);
    } else if (checks.isUpsell(myObservation ?? undefined)) {
      styles.push(CopilotCardStackStyles[type].upsell);
    }
  }

  return styles;
};
